/**
 * Theme-mode resolver — collapses :type:`Theme` to :type:`ResolvedTheme`.
 *
 * ``"light"`` and ``"dark"`` resolve to themselves; ``"system"``
 * resolves to whatever ``prefers-color-scheme`` evaluates to in
 * the current browser. The resolver is split into a pure function
 * (used by the inline pre-bootstrap script's TypeScript twin —
 * see :func:`resolveTheme`) and a React hook
 * (:func:`useResolvedTheme`) that subscribes to ``matchMedia``
 * changes so the DOM updates without a page reload per REQ-UI-041.
 *
 * Cross-references:
 *   - REQ-UI-040 (three theme modes).
 *   - REQ-UI-041 (system theme reactive to OS changes).
 *   - REQ-UI-044 (pre-bootstrap script — the inline script in
 *     ``index.html`` is the runtime equivalent of this resolver;
 *     keeping the logic in TS lets us unit-test the behaviour
 *     and lets future Stream A / Stream C extensions share it).
 *   - ADR-022 §Feature 2 — Map tiles in dark mode (REQ-UI-045 will
 *     subscribe to the same resolver to switch tile styles).
 */
import { useEffect, useState } from "react";
import type { ResolvedTheme, Theme } from "@/theme/tokens";

/**
 * Synchronous one-shot resolver. Returns the :type:`ResolvedTheme`
 * that should be applied to the DOM *right now* for the supplied
 * choice. ``"system"`` reads ``prefers-color-scheme`` from
 * ``window.matchMedia``; falls back to ``"light"`` when the API
 * is unavailable (SSR / jsdom without ``matchMedia`` polyfill).
 */
export function resolveTheme(choice: Theme): ResolvedTheme {
  if (choice === "light" || choice === "dark") {
    return choice;
  }
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * React hook variant — returns the resolved theme and re-renders
 * the consumer when ``prefers-color-scheme`` flips (only relevant
 * when ``choice === "system"``).
 *
 * The DOM ``data-theme`` attribute is applied by
 * :func:`useApplyTheme` in ``apply-theme.tsx``; this hook only
 * surfaces the resolved value for consumers that need it (map
 * tile-style selection per REQ-UI-045, MapLibre style overrides,
 * portal contents rendered outside the React tree).
 */
export function useResolvedTheme(choice: Theme): ResolvedTheme {
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(choice));

  useEffect(() => {
    // Recompute immediately when ``choice`` changes — covers both
    // a fresh choice (light → dark) and a stale ``"system"``
    // subscription after the user switches off system mode.
    setResolved(resolveTheme(choice));

    if (choice !== "system") {
      return;
    }
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent): void => {
      setResolved(event.matches ? "dark" : "light");
    };
    // ``addEventListener`` is the modern API; older Safari versions
    // (< 14) shipped ``addListener``/``removeListener`` only — we
    // target REQ-UI-038 modern Chromium, so the modern API is fine.
    mql.addEventListener("change", onChange);
    return () => {
      mql.removeEventListener("change", onChange);
    };
  }, [choice]);

  return resolved;
}
